import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ScheduleConflictItem } from "@myapp/contract";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { routes } from "../../../shared/config/routes";
import { useAbsenceDetail, useUpdateAbsence, useDeleteAbsence, getAbsenceCategoryLabel } from "../../../entities/absence";
import Loader from "../../../shared/ui/feedback/Loader/Loader";
import {
  TimeManagementFormWidget,
  toUpdateDto,
  fromTimeManagementDetail,
  type TimeManagementFormValues,
} from "../../../features/time-management";
import { useSuccessDialog } from "../../../shared/hooks/useSuccessDialog";
import { SuccessDialog } from "../../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { ConflictDialog } from "../../../shared/ui/feedback/ConflictDialog/ConflictDialog";
import { BlockerDialog } from "../../../shared/ui/feedback/BlockerDialog/BlockerDialog";
import { parseConflictsFromError } from "../../../shared/lib/errors/parseConflictsFromError";
import { parseCompletedBlockersFromError } from "../../../shared/lib/errors/parseCompletedBlockersFromError";
import { useDeleteDialog } from "../../../shared/hooks/useDeleteDialog";
import DeleteItemDialog from "../../../shared/ui/feedback/DeleteItemDialog/DeleteItemDialog";
import { ReadOnlyNotice } from "../../../shared/ui/feedback/ReadOnlyNotice/ReadOnlyNotice";
import { useCanPlanAllAbsences } from "../../../shared/hooks/useUserRoles";

export function TimeManagementEditPage() {
  const navigate = useNavigate();
  const { absenceId } = useParams<{ absenceId: string }>();
  const id = Number(absenceId);

  /**
   * Without the planner roles this page is a viewer: no Speichern, no Löschen, every field
   * read-only. The entry stays reachable — seeing when a colleague is away is the point of the
   * list — but changing it is not on offer.
   */
  const canEdit = useCanPlanAllAbsences();

  const { data, isLoading } = useAbsenceDetail(id);
  const updateMutation = useUpdateAbsence();
  const deleteMutation = useDeleteAbsence();
  const submitRef = useRef<(() => void) | null>(null);
  const { showSuccess, dialogProps } = useSuccessDialog();
  const deleteDialog = useDeleteDialog();

  const [conflicts, setConflicts] = useState<ScheduleConflictItem[] | null>(null);
  const [blockers, setBlockers] = useState<ScheduleConflictItem[] | null>(null);
  const pendingValuesRef = useRef<TimeManagementFormValues | null>(null);

  if (isLoading || !data) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  const handleSubmit = async (values: TimeManagementFormValues, force = false) => {
    try {
      const dto = toUpdateDto(values, force);
      await updateMutation.mutateAsync({ id, dto });
      showSuccess("Erfolgreich gespeichert", () => navigate(routes.zeitmanagement.root));
    } catch (err) {
      const blocking = parseCompletedBlockersFromError(err);
      if (blocking) {
        setBlockers(blocking);
        return;
      }
      const detected = parseConflictsFromError(err);
      if (detected) {
        pendingValuesRef.current = values;
        setConflicts(detected);
      } else {
        throw err;
      }
    }
  };

  const handleConflictConfirm = async () => {
    if (!pendingValuesRef.current) return;
    await handleSubmit(pendingValuesRef.current, true);
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  const handleConflictCancel = () => {
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  return (
    <>
      <PageLayout
        pageTitle={`${getAbsenceCategoryLabel(data.category)} ${canEdit ? "bearbeiten" : "ansehen"}`}
        scrollable
        back
        backTo={routes.zeitmanagement.root}
        primaryAction={canEdit ? {
          label: "Speichern",
          onClick: () => submitRef.current?.(),
          loading: updateMutation.isPending,
        } : undefined}
        removeAction={canEdit ? {
          onClick: () =>
            deleteDialog.openDelete("Eintrag wirklich löschen?", async () => {
              await deleteMutation.mutateAsync(id);
              navigate(routes.zeitmanagement.root);
            }),
        } : undefined}
      >
        {!canEdit && <ReadOnlyNotice />}
        <TimeManagementFormWidget
          category={data.category}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isPending}
          submitRef={submitRef}
          initialValues={fromTimeManagementDetail(data)}
          readOnly={!canEdit}
        />
      </PageLayout>
      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => { if (!open) dialogProps.onClose(); }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
      <ConflictDialog
        open={conflicts !== null}
        conflicts={conflicts ?? []}
        onConfirm={handleConflictConfirm}
        onCancel={handleConflictCancel}
        isLoading={updateMutation.isPending}
      />
      <BlockerDialog
        open={blockers !== null}
        blockers={blockers ?? []}
        onClose={() => setBlockers(null)}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
      />
      <DeleteItemDialog deleteModal={deleteDialog} />
    </>
  );
}
